import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../utils/api';

// Get full system config
export const useSystemConfig = () => {
  return useQuery({
    queryKey: ['systemConfig'],
    queryFn: async () => {
      const response = await api.get('/system/config');
      return response.data.data;
    },
    staleTime: 30000,
    refetchInterval: 30000,
  });
};

// Get a specific config section
export const useSystemConfigSection = (section) => {
  return useQuery({
    queryKey: ['systemConfig', section],
    queryFn: async () => {
      const response = await api.get(`/system/config/${section}`);
      return response.data.data;
    },
    enabled: !!section,
    staleTime: 30000,
  });
};

// Update system config
export const useUpdateSystemConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updates) => {
      const response = await api.put('/system/config', updates);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['systemConfig'] });
    },
  });
};

// Get public config (no auth required)
export const usePublicConfig = () => {
  return useQuery({
    queryKey: ['publicConfig'],
    queryFn: async () => {
      const response = await api.get('/system/config/public');
      return response.data.data;
    },
    staleTime: 60000,
  });
};

// Get config audit log
export const useConfigAuditLog = (page = 1, limit = 50) => {
  return useQuery({
    queryKey: ['configAuditLog', page, limit],
    queryFn: async () => {
      const response = await api.get(`/system/config/audit-log?page=${page}&limit=${limit}`);
      return response.data.data;
    },
    staleTime: 10000,
  });
};

// Export config
export const useExportConfig = () => {
  return useMutation({
    mutationFn: async () => {
      const response = await api.get('/system/config/export');
      return response.data.data;
    },
  });
};

// Import config
export const useImportConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (importData) => {
      const response = await api.post('/system/config/import', importData);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['systemConfig'] });
      queryClient.invalidateQueries({ queryKey: ['configAuditLog'] });
    },
  });
};

// Reset config section
export const useResetConfigSection = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (section) => {
      const response = await api.post(`/system/config/reset/${section}`);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['systemConfig'] });
      queryClient.invalidateQueries({ queryKey: ['configAuditLog'] });
    },
  });
};

// Config impact preview
export const useConfigImpactPreview = () => {
  return useMutation({
    mutationFn: async (proposedChanges) => {
      const response = await api.post('/system/config/impact-preview', proposedChanges);
      return response.data.data;
    },
  });
};
